import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Add, Error } from "@material-ui/icons";
import { Input, Tabs } from "antd";
import Modal from "antd/lib/modal/Modal";
import { hide } from "store/features/userDocumentation/documentationSlice";
import { RootState } from "store/rootReducer";
import { useDebounce } from "use-debounce";

import { useUser } from "hooks";

import { useGetTutorials } from "api/tutorials";

import styles from "components/user-documentation/styles/documentationModal.module.scss";

import TagSelect from "./TagSelect";
import DocumentationViewRenderer from "./documentation-modal-components/DocumentationViewRenderer";
import NotificationSidebar from "./documentation-modal-components/NotificationSidebar";
import TutorialSidebar from "./documentation-modal-components/TutorialSidebar";
import { Category, DocumentationTab, DocumentationView, NotificationItem } from "./types";

// How long to wait after the user stops typing before searching.
const SEARCH_DEBOUNCE_MS = 300;

// Invites a phrase rather than a single word — the search understands both —
// and names two topics the material actually covers.
const SEARCH_PLACEHOLDER = "Describe what you want to find (e.g. 'type curves' or 'xda')";

const DocumentationModal = () => {
  const { display: displayDocumentationModal, defaultTab } = useSelector(
    (state: RootState) => state.documentation
  );
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState<DocumentationTab>(
    DocumentationTab.Tutorials
  );
  const [activeView, setActiveView] = useState<DocumentationView>(
    DocumentationView.TutorialTileView
  );
  const [selectedItemId, setSelectedItemId] = useState<string>();
  const [selectedCategory, setSelectedCategory] = useState<Category>(
    Category.ChartsAndDataVisualization
  );
  const { isTuringAdmin } = useUser();
  const [search, setSearch] = useState("");
  // Wait for a pause in typing before searching, so a five-letter word costs
  // one request instead of five.
  const [debouncedTerm, debounceControl] = useDebounce(search, SEARCH_DEBOUNCE_MS);
  // An empty box means "show everything", which needs no pause — and waiting
  // would leave a newly opened tab filtered by the term typed on the last one.
  const debouncedSearch = search === "" ? "" : debouncedTerm;
  const [tags, setTags] = useState<string[]>([]);

  const {
    data: tutorials = [],
    error: tutorialsError,
    refetch: refetchTutorials
  } = useGetTutorials(debouncedSearch, activeTab === DocumentationTab.Tutorials);

  // Refetch tutorials when modal opens to ensure latest data
  useEffect(() => {
    if (displayDocumentationModal) {
      refetchTutorials();
    }
  }, [displayDocumentationModal, refetchTutorials]);

  // Set the active tab based on defaultTab when modal opens
  useEffect(() => {
    if (displayDocumentationModal && defaultTab) {
      setActiveTab(defaultTab as DocumentationTab);
    }
  }, [displayDocumentationModal, defaultTab]);

  // Error handling for notifications (still uses legacy state management)
  const [notificationsError, setNotificationsError] = useState("");

  // Combined error message for display (generic user-friendly messages)
  const getLoadPostsError = () => {
    switch (activeTab) {
      case DocumentationTab.Tutorials:
        return tutorialsError ? "Error loading tutorials" : "";
      case DocumentationTab.Notifications:
        return notificationsError;
      default:
        return "";
    }
  };
  const loadPostsError = getLoadPostsError();

  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [hasInitializedNotificationSelection, setHasInitializedNotificationSelection] =
    useState(false);

  // Cache for the currently selected notification to preserve data when scrolling/pagination changes
  const [selectedNotificationCache, setSelectedNotificationCache] =
    useState<NotificationItem | null>(null);

  // Update view when tab changes
  useEffect(() => {
    if (activeTab === DocumentationTab.Tutorials) {
      setActiveView(DocumentationView.TutorialTileView);
      setSelectedItemId(undefined);
      setHasInitializedNotificationSelection(false);
    } else {
      setActiveView(DocumentationView.NotificationPostView);
      // Auto-select first notification if available and we haven't initialized yet
      if (notifications.length > 0 && !hasInitializedNotificationSelection) {
        setSelectedItemId(notifications[0].id.toString());
        setHasInitializedNotificationSelection(true);
      } else if (notifications.length === 0) {
        setSelectedItemId(undefined);
      }
    }
  }, [activeTab, notifications, hasInitializedNotificationSelection]);

  // Auto-select first notification when notifications are loaded for the notifications tab
  // This should only run when switching to notifications tab for the first time or when notifications are initially loaded
  useEffect(() => {
    if (
      activeTab === DocumentationTab.Notifications &&
      notifications.length > 0 &&
      !selectedItemId &&
      activeView === DocumentationView.NotificationPostView
    ) {
      setSelectedItemId(notifications[0].id.toString());
      setHasInitializedNotificationSelection(true);
    }
  }, [notifications, activeTab, selectedItemId, activeView]);

  // Cache the selected notification to preserve data during pagination/scrolling
  useEffect(() => {
    if (selectedItemId && activeTab === DocumentationTab.Notifications) {
      const selectedNotification = notifications.find(
        (n) => n.id.toString() === selectedItemId
      );
      if (selectedNotification) {
        setSelectedNotificationCache(selectedNotification);
      }
    } else if (!selectedItemId) {
      setSelectedNotificationCache(null);
    }
  }, [selectedItemId, notifications, activeTab]);

  const handleTabChange = (key: string) => {
    setActiveTab(key as DocumentationTab);
    // Clear the term when moving tabs, so the other tab does not open already
    // filtered by something the user typed for this one.
    setSearch("");
    debounceControl.cancel();
  };

  const handleViewChange = (view: DocumentationView) => {
    setActiveView(view);
  };

  const handleSelectItem = (itemId: string) => {
    setSelectedItemId(itemId);
  };

  const handleSaveNotification = (notification: NotificationItem) => {
    // TODO: Implement actual save logic
    setNotifications((prev) => {
      const index = prev.findIndex((n) => n.id === notification.id);
      if (index >= 0) {
        // Update existing
        return prev.map((n) => (n.id === notification.id ? notification : n));
      } else {
        // Add new
        return [...prev, notification];
      }
    });
  };

  const handleDeleteNotification = (notificationId: number | string) => {
    setNotifications((prev) => {
      const updatedNotifications = prev.filter(
        (n) => n.id.toString() !== notificationId.toString()
      );

      // If we deleted the currently selected item, select the first remaining item
      if (selectedItemId === notificationId.toString()) {
        if (updatedNotifications.length > 0) {
          setSelectedItemId(updatedNotifications[0].id.toString());
          setSelectedNotificationCache(updatedNotifications[0]);
        } else {
          setSelectedItemId(undefined);
          setSelectedNotificationCache(null);
        }
      }

      return updatedNotifications;
    });
  };

  const handleCategoryChange = (category: Category) => {
    setSelectedCategory(category);
    // When a category is selected, switch to TutorialTileView
    setActiveView(DocumentationView.TutorialTileView);
    setSelectedItemId(undefined); // Clear any selected item
  };

  const hideSidebar = activeView === DocumentationView.NotificationEditView;

  return (
    <Modal
      className={styles.modalContainer}
      open={displayDocumentationModal}
      onCancel={() => {
        dispatch(hide());
      }}
      footer={null}
      centered
      width={"70%"}
      // Not `data-testid` directly on `Modal` (T2 review finding): rc-dialog's
      // `Dialog` only forwards data-* attributes to the outer `.ant-modal-root`
      // wrapper (`pickAttrs(props, { data: true })` in rc-dialog's Dialog),
      // whose own box is `position: fixed` with zero height — its children are
      // sized, it isn't. `wrapProps` spreads onto `.ant-modal-wrap` instead,
      // which is the fixed, viewport-covering, actually-visible box (and the
      // one Tour.scss's passthrough z-index rule already lifts).
      wrapProps={{ "data-testid": "documentation-modal" }}>
      <Tabs
        defaultActiveKey={DocumentationTab.Tutorials}
        activeKey={activeTab}
        onTabClick={handleTabChange}
        data-testid="documentation-tabs"
        items={[
          {
            key: DocumentationTab.Tutorials,
            label: "Tutorials"
          },
          {
            key: DocumentationTab.Notifications,
            label: "Notifications"
          }
        ]}
      />

      {/* Named for the guided walkthrough: the Tabs above render a strip only
          (their items carry no children), so there is no `.ant-tabs-tabpane` to
          point at and this div is what each tab actually shows. */}
      <div className={styles.tabContentContainer} data-testid="documentation-tab-content">
        {!hideSidebar && (
          <div className={styles.sidebarContainer}>
            {/* Everyone searches. The gate below is about who may add material,
                and it used to hide this whole header — including the search box
                — from customers on the tutorials tab, which is the tab the
                modal opens on. Customers are who reported search as poor. */}
            <div className={`${styles.documentationHeader} ${styles.sidebarSection}`}>
              {isTuringAdmin && (
                <button
                  className={styles.newPostButton}
                  data-testid="newPost-button"
                  onClick={() => {
                    const editView =
                      activeTab === DocumentationTab.Tutorials
                        ? DocumentationView.TutorialEditView
                        : DocumentationView.NotificationEditView;
                    setActiveView(editView);
                    setSelectedItemId(undefined);
                  }}>
                  <Add fontSize="large" />
                  New
                </button>
              )}

              <Input
                allowClear
                value={search}
                autoFocus
                onChange={(e) => setSearch(e.target.value)}
                placeholder={SEARCH_PLACEHOLDER}
              />

              {/* Tags apply to notifications only; videos are not tagged. */}
              {activeTab === DocumentationTab.Notifications && (
                <TagSelect
                  id="notification-tags"
                  placeholder="Filter by tag"
                  value={tags}
                  onChange={(v) => setTags(v)}
                />
              )}
            </div>

            <div
              data-testid="documentation-list"
              className={`${styles.documentationList} ${styles.sidebarSection}`}>
              {loadPostsError !== "" && (
                <div
                  className={styles.errorContainer}
                  data-testid="tutorials-error-message">
                  <Error style={{ top: 3 }} /> {loadPostsError}
                </div>
              )}

              {activeTab === DocumentationTab.Notifications ? (
                <>
                  <NotificationSidebar
                    search={debouncedSearch}
                    tags={tags}
                    setLoadPostsError={setNotificationsError}
                    onClick={(postId: string) => {
                      setSelectedItemId(postId);
                      setActiveView(DocumentationView.NotificationPostView);
                    }}
                    onNotificationsUpdate={setNotifications}
                    selectedItemId={selectedItemId}
                  />
                </>
              ) : (
                <>
                  <TutorialSidebar
                    selectedCategory={selectedCategory}
                    onCategoryChange={handleCategoryChange}
                  />
                </>
              )}
            </div>
          </div>
        )}

        <div
          className={`${styles.modalBodyContent} ${hideSidebar ? styles.fullWidth : ""}`}>
          <DocumentationViewRenderer
            activeView={activeView}
            selectedItemId={selectedItemId}
            selectedCategory={selectedCategory}
            searchTerm={debouncedSearch}
            tutorials={tutorials}
            notifications={notifications}
            selectedNotificationCache={selectedNotificationCache}
            canEdit={isTuringAdmin}
            onViewChange={handleViewChange}
            onSelectItem={handleSelectItem}
            onSaveNotification={handleSaveNotification}
            onDeleteNotification={handleDeleteNotification}
          />
        </div>
      </div>
    </Modal>
  );
};

export default DocumentationModal;
